import { AccordionSummary, CircularProgress, Divider, ListItem, ListItemAvatar, ListItemText, Skeleton, Typography } from '@mui/material';
import { Add, KeyboardArrowDown } from '@mui/icons-material';
import { Box } from '@mui/system';
import { useEffect, useState, useRef } from 'react'; // Import useRef
import { useLazyQuery } from '@apollo/client';
import Accordion from '@mui/material/Accordion';
import Card from '@mui/material/Card';
import List from '@mui/material/List';

import { AvatarImage } from '../Avatar';
import { CREATE_WORKSPACE, PROJECTS } from '../../constants/routes';
import { DOMAIN_NAME } from '../../constants/config';
import { GET_USER_WORKSPACES } from '../../apis/workspace';
import { navigateToBaseUrl, navigateToWorkspaceUrl } from '../../utils/navigator';
import { useAuth } from '../../context/AuthContext';
import Button from '../atoms/button';
import useWorkspace from '../../context/WorkspaceContext';

const NoWorkspacesMessage = () => (
  <div className="no-workspace-found-container">
    <p className="no-workspace-found-title">No workspace found! </p>
    <p className="no-workspace-found-subtitle">Switch between them by creating a new workspace!</p>
  </div>
);

const WorkspaceDropdown = () => {
  // init
  const { authUser } = useAuth();
  const { workspace } = useWorkspace();

  // states
  const [isExpanded, setIsExpanded] = useState(false);
  const [workspaces, setWorkspaces] = useState([]);

  // mutations/queries
  const [getWorkspaces, { loading }] = useLazyQuery(GET_USER_WORKSPACES);

  // constants
  const dropdownRef = useRef(null);

  const handleExpansion = () => {
    setIsExpanded(!isExpanded);
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsExpanded(false);
    }
  };

  const createWorkspaceHandler = () => {
    setIsExpanded(false);
    navigateToBaseUrl(`/${CREATE_WORKSPACE}`, true);
  };

  const handleWorkspaceSwitch = (workspaceItem) => {
    setIsExpanded(false);
    if (workspace.id !== workspaceItem.id) navigateToWorkspaceUrl(workspaceItem.handle, `/${PROJECTS}`, true);
  };

  useEffect(() => {
    const fetchWorkspaces = async () => {
      const { data: { listWorkspace: workspacesData } } = await getWorkspaces({ variables: { userId: authUser.id } });
      const filteredWorkspaces = workspacesData.filter((workspaceItem) => workspaceItem.id !== workspace.id);
      filteredWorkspaces.unshift({ ...workspace });
      setWorkspaces(filteredWorkspaces);
    };
    if (isExpanded) fetchWorkspaces();
  }, [isExpanded]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div ref={dropdownRef} onClick={handleExpansion}>
      <div className="workspace-dropdown-avatar">
        {
          workspace
            ? <AvatarImage squareBorder whiteAvatar alt="Workspace Logo" name={workspace?.name} src={workspace?.logo} fontWeight={600} fontSize={22} height={44} width={44} />
            : <Skeleton sx={{ bgcolor: 'white' }} animation="wave" variant="rounded" width={44} height={44} />
        }
      </div>
      <Box className="workspace-dropdown-box-1" />
      <Box className="workspace-dropdown-box-2" />
      <Accordion className="workspace-dropdown" expanded={isExpanded}>
        <AccordionSummary
          expandIcon={<KeyboardArrowDown style={{ fill: 'white' }} />}
          className="accordion-summary"
        />
        <Card className="dropdown-card">
          <List className="dropdown-list">
            {!loading && workspaces.length === 0 && <NoWorkspacesMessage />}
            {loading && <div className="loading-container"><CircularProgress size={30} /></div>}
            {workspaces.map((workspaceItem) => {
              const isSelectedItem = workspaceItem.id === workspace.id;
              return (
                <ListItem key={`${workspaceItem.handle}`} className="dropdown-listitem" onClick={() => handleWorkspaceSwitch(workspaceItem)}>
                  <ListItemAvatar data-selected={isSelectedItem} className="dropdown-avatar">
                    <AvatarImage whiteAvatar squareBorder borderGray={!isSelectedItem} placeHolder="WS" alt="Workspace Logo" name={workspaceItem.name} src={workspaceItem.logo} fontWeight={600} fontSize={20} height={isSelectedItem ? 36 : 42} width={isSelectedItem ? 36 : 42} />
                  </ListItemAvatar>
                  <ListItemText
                    className="dropdown-listitem-text"
                    primary={workspaceItem.name}
                    secondary={<Typography className="dropdown-listitem-text-secondary">{`${workspaceItem.handle}.${DOMAIN_NAME}`}</Typography>}
                  />
                </ListItem>
              );
            })}
          </List>
          <div className="dropdown-footer">
            <Divider className="dropdown-divider" variant="middle" />
            <Button fullWidth className="dropdown-create-workspace" primary onClick={createWorkspaceHandler}>
              <Add />
              &nbsp;&nbsp;Create a Workspace
            </Button>
          </div>
        </Card>
      </Accordion>
    </div>
  );
};

export default WorkspaceDropdown;
